import { api } from "@/app/services/api.service";

export const FEATURE_FLAGS_PATH = "configuration/feature-flags";
export const FEATURE_FLAGS_AUDIT_PATH = "configuration/feature-flags/audit";

export const FEATURE_FLAG_STATUSES = ["ENABLED", "DISABLED"] as const;
export type FeatureFlagStatus = (typeof FEATURE_FLAG_STATUSES)[number];

export type FeatureFlagMetadata = {
  description: string | null;
  endpoints: string[];
};

export type FeatureFlag = {
  name: string;
  status: FeatureFlagStatus;
  metadata: FeatureFlagMetadata;
};

export type FeatureFlagPayload = {
  name: string;
  status: FeatureFlagStatus;
  metadata: FeatureFlagMetadata;
};

export function unwrapPayload(res: { data: unknown }) {
  const data = res.data as Record<string, unknown>;
  if (
    data != null &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "data") &&
    data.data != null
  ) {
    return data.data;
  }
  return data;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as { response?: { data?: { message?: string } } };
  return err.response?.data?.message || fallback;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

function parseMetadata(raw: unknown): FeatureFlagMetadata {
  if (raw == null || typeof raw !== "object") {
    return { description: null, endpoints: [] };
  }
  const record = raw as Record<string, unknown>;
  const description =
    record.description != null && record.description !== ""
      ? String(record.description)
      : null;
  const endpointsRaw = record.endpoints;
  const endpoints = Array.isArray(endpointsRaw)
    ? endpointsRaw.map((item) => String(item).trim()).filter(Boolean)
    : [];
  return { description, endpoints };
}

export function parseFeatureFlag(body: unknown): FeatureFlag | null {
  if (body == null || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  const name = readString(record, ["name", "flagName"]);
  if (!name) return null;
  const statusRaw = readString(record, ["status"])?.toUpperCase();
  const status: FeatureFlagStatus =
    statusRaw === "ENABLED" ? "ENABLED" : "DISABLED";
  return {
    name,
    status,
    metadata: parseMetadata(record.metadata),
  };
}

export function parseFeatureFlagList(body: unknown): FeatureFlag[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body.map(parseFeatureFlag).filter((item): item is FeatureFlag => item != null);
  }
  if (typeof body !== "object") return [];
  const obj = body as Record<string, unknown>;
  const candidates = [obj.content, obj.items, obj.flags, obj.records, obj.data];
  for (const candidate of candidates) {
    const parsed = parseFeatureFlagList(candidate);
    if (parsed.length > 0) return parsed;
  }
  const single = parseFeatureFlag(body);
  return single ? [single] : [];
}

export function extractAuditList(body: unknown): Record<string, unknown>[] {
  if (body == null) return [];
  if (Array.isArray(body)) {
    return body.filter((item) => item != null && typeof item === "object") as Record<
      string,
      unknown
    >[];
  }
  const obj = body as Record<string, unknown>;
  const candidates = [obj.auditLogs, obj.logs, obj.items, obj.content, obj.records, obj.data];
  for (const candidate of candidates) {
    const parsed = extractAuditList(candidate);
    if (parsed.length > 0) return parsed;
  }
  return [];
}

export async function fetchFeatureFlags(): Promise<FeatureFlag[]> {
  const res = await api().get(FEATURE_FLAGS_PATH);
  return parseFeatureFlagList(unwrapPayload(res));
}

export async function createFeatureFlag(payload: FeatureFlagPayload) {
  await api().post(FEATURE_FLAGS_PATH, payload);
}

export async function updateFeatureFlag(name: string, payload: Omit<FeatureFlagPayload, "name">) {
  await api().put(`${FEATURE_FLAGS_PATH}/${encodeURIComponent(name)}`, {
    name,
    ...payload,
  });
}

export async function deleteFeatureFlag(name: string) {
  await api().delete(`${FEATURE_FLAGS_PATH}/${encodeURIComponent(name)}`);
}

export async function fetchFeatureFlagsAudit(
  flagName?: string | null
): Promise<Record<string, unknown>[]> {
  const params: Record<string, string> = {};
  if (flagName) params.flagName = flagName;
  const res = await api().get(FEATURE_FLAGS_AUDIT_PATH, { params });
  const root = res.data;
  if (Array.isArray(root)) return extractAuditList(root);
  if (root != null && typeof root === "object") {
    const rootObj = root as Record<string, unknown>;
    if (Array.isArray(rootObj.data)) return extractAuditList(rootObj.data);
    const fromUnwrapped = extractAuditList(unwrapPayload(res));
    if (fromUnwrapped.length > 0) return fromUnwrapped;
    return extractAuditList(root);
  }
  return [];
}
